import { useEffect, useLayoutEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  HALF_ANIMATION_DURATION,
  POPUP_QUESTION_KEYS,
  PROGRESS_MIDPOINTS,
  greenAccept,
  redCross,
  reviewAvatar,
  reviewStars,
} from '~/pages/anatomy/preparePlan/libs/components/PreparePlanAnatomy/constants'
import { Styles as S } from '~/pages/anatomy/preparePlan/libs/components/PreparePlanAnatomy/styles'
import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { ANATOMY_V2_ACCENT_COLOR } from '~/pages/anatomyV2/libs/constants'
import ProgressAnalyzer from '~/shared/components/ProgressAnalyzer'
import { Cohort } from '~/shared/constants/commonConstants'
import { COLORS } from '~/shared/constants/uiConstants'
import { useDelayedContinue } from '~/shared/hooks/useDelayedContinue'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { useSegmentAnimation } from '~/shared/hooks/useSegmentAnimation'
import { eventLogger } from '~/shared/services/eventLogger'
import { selectCohortID } from '~/shared/store/common/selects'
import { TPageProps } from '~/shared/types/commonTypes'

const ANATOMY_V2_SECTION_NUMBER = 6
const ANATOMY_V2_POPUP_MIDPOINT = 60

const getPopupQuestionKeys = (questionKey: string, cohortId: string) => [
  `onboarding.preparePlanAnatomy.popup.${questionKey}_${cohortId}`,
  `onboarding.preparePlanAnatomy.popup.${questionKey}`,
]

const PreparePlanAnatomy = ({ nextPagePath, pageId }: TPageProps) => {
  const { i18n } = useTranslation()
  const cohortId = useSelector(selectCohortID)
  const isAnatomyV2 = cohortId === Cohort.ANATOMY_V2
  const progressColor = isAnatomyV2
    ? ANATOMY_V2_ACCENT_COLOR
    : COLORS.PURPLE_400

  useLayoutEffect(() => {
    if (!isAnatomyV2) return undefined

    document.documentElement.style.setProperty('--header-bg', COLORS.GRAY_100)

    return () => {
      document.documentElement.style.removeProperty('--header-bg')
    }
  }, [isAnatomyV2])

  const [activePopup, setActivePopup] = useState<0 | 1 | 2 | 3 | null>(null)

  const { handleChange } = useDelayedContinue()
  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'Creating your personalized learning plan',
  })
  const goToNextPage = () => handleChange('', handleContinue)

  const {
    displayValue: d1,
    slowValue: s1,
    isFinished: fin1,
    start: start1,
    resume: resume1,
  } = useSegmentAnimation({
    halfDuration: HALF_ANIMATION_DURATION,
    midpoint: isAnatomyV2 ? ANATOMY_V2_POPUP_MIDPOINT : PROGRESS_MIDPOINTS[0],
    onMidpoint: () => setActivePopup(0),
  })

  const {
    displayValue: d2,
    slowValue: s2,
    isFinished: fin2,
    start: start2,
    resume: resume2,
  } = useSegmentAnimation({
    halfDuration: HALF_ANIMATION_DURATION,
    midpoint: PROGRESS_MIDPOINTS[1],
    onMidpoint: isAnatomyV2 ? undefined : () => setActivePopup(1),
  })

  const {
    displayValue: d3,
    slowValue: s3,
    isFinished: fin3,
    start: start3,
    resume: resume3,
  } = useSegmentAnimation({
    halfDuration: HALF_ANIMATION_DURATION,
    midpoint: PROGRESS_MIDPOINTS[2],
    onMidpoint: isAnatomyV2 ? undefined : () => setActivePopup(2),
  })

  const {
    displayValue: d4,
    slowValue: s4,
    start: start4,
    resume: resume4,
  } = useSegmentAnimation({
    halfDuration: HALF_ANIMATION_DURATION,
    midpoint: isAnatomyV2 ? ANATOMY_V2_POPUP_MIDPOINT : PROGRESS_MIDPOINTS[3],
    onMidpoint: () => setActivePopup(3),
    onComplete: goToNextPage,
  })

  useEffect(() => {
    eventLogger.logGenerationPlanScreenShow()
    start1()
  }, [start1])

  useEffect(() => {
    if (fin1) start2()
  }, [fin1, start2])

  useEffect(() => {
    if (fin2) start3()
  }, [fin2, start3])

  useEffect(() => {
    if (fin3) start4()
  }, [fin3, start4])

  const resumeFns = [resume1, resume2, resume3, resume4]

  const handleAnswer = (answer: string) => {
    const popup = activePopup
    setActivePopup(null)
    if (popup !== null) {
      const question = i18n.getFixedT('en')(
        getPopupQuestionKeys(POPUP_QUESTION_KEYS[popup], cohortId),
      )

      eventLogger.logQuestion({
        question,
        answers: answer,
        pageName: pageId,
      })
      eventLogger.logGenerationPlanQuestionCompleted(question, answer)
      resumeFns[popup]()
    }
  }

  return (
    <S.Root>
      <S.StyledContainer>
        {isAnatomyV2 && (
          <SectionProgress
            currentSection={ANATOMY_V2_SECTION_NUMBER}
            pageId={pageId}
          />
        )}

        <S.Title $isAnatomyV2={isAnatomyV2}>
          <Trans i18nKey="onboarding.preparePlanAnatomy.title" />
        </S.Title>

        <S.ProgressContainer>
          <ProgressAnalyzer color={progressColor} value={d1} numberValue={s1}>
            <Trans i18nKey="onboarding.preparePlanAnatomy.goalsAligning" />
          </ProgressAnalyzer>

          <ProgressAnalyzer color={progressColor} value={d2} numberValue={s2}>
            <Trans i18nKey="onboarding.preparePlanAnatomy.learningStyle" />
          </ProgressAnalyzer>

          <ProgressAnalyzer color={progressColor} value={d3} numberValue={s3}>
            <Trans i18nKey="onboarding.preparePlanAnatomy.planBuilding" />
          </ProgressAnalyzer>

          <ProgressAnalyzer color={progressColor} value={d4} numberValue={s4}>
            <Trans i18nKey="onboarding.preparePlanAnatomy.prioritizingChallenges" />
          </ProgressAnalyzer>
        </S.ProgressContainer>

        <S.EnjoyTitle>
          <Trans i18nKey="onboarding.preparePlanAnatomy.trustedBy" />
        </S.EnjoyTitle>

        <S.Review>
          <S.ReviewHeader>
            <S.ReviewAvatar src={reviewAvatar} alt="reviewer" />

            <S.ReviewNameBlock>
              <S.ReviewAuthor>
                <Trans i18nKey="onboarding.preparePlanAnatomy.reviewAuthor" />
              </S.ReviewAuthor>
              <S.ReviewStars src={reviewStars} alt="5 stars" />
            </S.ReviewNameBlock>
          </S.ReviewHeader>

          <S.ReviewText>
            <Trans i18nKey="onboarding.preparePlanAnatomy.reviewText" />
          </S.ReviewText>
        </S.Review>
      </S.StyledContainer>

      {activePopup !== null && (
        <S.Overlay>
          <S.PopupCard>
            <S.PopupHeader>
              <Trans i18nKey="onboarding.preparePlanAnatomy.popup.header" />
            </S.PopupHeader>

            <S.PopupQuestion>
              <Trans
                i18nKey={getPopupQuestionKeys(
                  POPUP_QUESTION_KEYS[activePopup],
                  cohortId,
                )}
              />
            </S.PopupQuestion>

            <S.PopupButtonWrapper>
              <S.PopupButton type="button" onClick={() => handleAnswer('no')}>
                <S.ButtonIcon src={redCross} alt="no" />
                <Trans i18nKey="actions.no" />
              </S.PopupButton>

              <S.PopupButton type="button" onClick={() => handleAnswer('yes')}>
                <S.ButtonIcon src={greenAccept} alt="yes" />
                <Trans i18nKey="actions.yes" />
              </S.PopupButton>
            </S.PopupButtonWrapper>
          </S.PopupCard>
        </S.Overlay>
      )}
    </S.Root>
  )
}

export default PreparePlanAnatomy
